/* global React, firebase, CUSTOMERS, SUPPLIERS, PRODUCTS, INVOICES, CHART, DEADLINES, VAT, TRIAL_BALANCE, RESULT, EMPLOYEES, PAY_OVERVIEW, PAY_TERMS, PAY_HOLIDAY, PAY_PENSION, PAY_RUNS, PAY_TYPES, PAY_SETTINGS, runtime */
// Backend adapter for the redesigned prototype.
// It reuses the original Plumme backend contract:
// businesses/{businessId}/app/state => { state: AppState, updatedAt, ... }
// Domenelogikk som tidligere lå her er flyttet til backend-mappers.jsx,
// backend-accounting-engine.jsx, backend-dashboard-taxreturn.jsx,
// backend-payroll-travel.jsx, backend-bank-billing.jsx,
// backend-invoice-purchase-editor.jsx, backend-receipt-inbox.jsx og
// backend-invoice-actions.jsx (lastes rett før denne fila, se index.html).
// Denne fila holder ClientStore-tilgangen og selve PlummeBackend-API-et som
// samler funksjonene fra alle disse filene.

const clientStoreScopeMatches = (clientId, {
  configured = runtime.configured,
  activeBusinessId = runtime.activeBusinessId,
  hasState = Boolean(runtime.appState),
} = {}) => !configured || (hasState && clientId === activeBusinessId);
const getClientStoreValue = (clientId, key, fallback) => {
  if (!clientStoreScopeMatches(clientId)) return fallback;
  publishGlobals(runtime.appState);
  return clone((window.clientStoreCache && window.clientStoreCache[key]) || fallback);
};
window.clientStore = {
  get(client, key, fallback) {
    const normalized = key === 'drafts' ? 'drafts' : key;
    return getClientStoreValue(client, normalized, fallback);
  },
  set(client, key, value) {
    if (!clientStoreScopeMatches(client)) return;
    return setEntity(key, value);
  },
};

if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
  window.PlummeBackendTest = {
    attachHighConfidenceReceiptMatches,
    bookedReceiptDuplicateLineMatches,
    buildDashboardFollowupGroups,
  highConfidenceReceiptMatches,
  refreshBankSuggestionRules,
  getBookedReceiptDuplicateMatch,
    bankSettledEntryPosts,
    refreshBankReceiptMatches,
	    buildReceiptBookingPlan,
	    bankPurchaseEditorLineValues,
	    receiptAnalysisBookableNetAmount, buildManualPostingEntries, privatePurchaseReimbursementPosting: (...args) => window.PlummeFinancialWorkflows.privatePurchaseReimbursementPosting(...args), supplierCreditPosting: (...args) => window.PlummeFinancialWorkflows.supplierCreditPosting(...args), supplierCreditRefundPosting: (...args) => window.PlummeFinancialWorkflows.supplierCreditRefundPosting(...args),
    inboxAttachmentTargets,
    inboxAttachmentFileName,
    readUiDefaultState,
    mergeState,
    stripServerOwnedState,
    SERVER_OWNED_STATE_FIELDS,
    SERVER_OWNED_SETTINGS_FIELDS,
    mapCustomerToUi,
    mapInvoiceToUi,
    buildCreditNoteLines,
    creditableInvoiceLines,
    mapSupplierToUi,
    nextSupplierNumber,
    assignSupplierNumbers,
    normalizeBusinessBankSettings, assertBankLedgerControlLocal,
    deriveReports,
    deriveReportChart,
    deriveAccountingLedgerVouchers,
    derivePayroll,
    payrollTaxBankBookingRequiresPayrollFlow,
    resolveBankTaxPaymentAccountCode,
    payrollRunPayableEmployerContribution,
    buildPayrollPayslipSelection,
    normalizeUserPreferences,
    resolveAccountingScope,
    editorDraftKey,
    normalizeReceiptPaymentSourceDecision,
    applyReceiptPaymentSourceDecision,
    deleteReceiptInboxEntriesFromState,
    buildBankPageModelForState(state) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return buildBankPageModel();
      } finally {
        runtime.appState = previousState;
      }
    },
    buildReceiptInboxModelForState(state) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return buildReceiptInboxModel();
      } finally {
        runtime.appState = previousState;
      }
    },
    receiptInboxReviewForState(state, entry, paymentSourceHint) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return receiptInboxReview(entry, paymentSourceHint);
      } finally {
        runtime.appState = previousState;
      }
    },
    receiptInboxBankPaymentSourceCandidatesForState(state) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return receiptInboxBankPaymentSourceCandidates();
      } finally {
        runtime.appState = previousState;
      }
    },
    receiptSettlementOpenInvoiceMatchesForState(state, analysis) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return receiptSettlementOpenInvoiceMatchSuggestions(analysis, { state: runtime.appState });
      } finally {
        runtime.appState = previousState;
      }
    },
    receiptSettlementBankLineForState(state, entry, accountCode = '') {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        const analysis = entry?.receiptAnalysis || {};
        return receiptSettlementBankLineForInboxEntry({ entry, analysis, accountCode, state: runtime.appState });
      } finally {
        runtime.appState = previousState;
      }
    },
    receiptUploadDuplicatesForEntries(entries) {
      const list = safeArray(entries);
      const index = buildReceiptUploadDuplicateIndex(list);
      return list.map((entry) => ({ id: entry.id, duplicateUpload: receiptUploadDuplicateFor(entry, index) }));
    },
    findPossibleDuplicateExpensesForState(state, query) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return findPossibleDuplicateExpenses(query);
      } finally {
        runtime.appState = previousState;
      }
    },
    buildPurchaseFromEditorForState(state, editor) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return buildPurchaseFromEditor(editor);
      } finally {
        runtime.appState = previousState;
      }
    },
    invoiceDraftExistsForState(state, invoiceId) {
      const previousState = runtime.appState;
      try {
        runtime.appState = state;
        return invoiceDraftExists(invoiceId);
      } finally {
        runtime.appState = previousState;
      }
    },
    buildAiAdvisorModelForState(state, year) {
      const previousState = runtime.appState;
      const previousConfigured = runtime.configured;
      const previousUser = runtime.user;
      try {
        runtime.appState = mergeState({ ...state, activeYear: year || state?.activeYear });
        runtime.configured = true;
        runtime.user = runtime.user || { uid: 'test-user' };
        return buildAiAdvisorModelForYear(mergeState(runtime.appState), Number(year || state?.activeYear) || new Date().getFullYear());
      } finally {
        runtime.appState = previousState;
        runtime.configured = previousConfigured;
        runtime.user = previousUser;
      }
    },
    buildHomeDashboardModelForState(state) {
      const previousState = runtime.appState;
      const previousConfigured = runtime.configured;
      const previousUser = runtime.user;
      try {
        runtime.appState = mergeState(state);
        runtime.configured = true;
        runtime.user = runtime.user || { uid: 'test-user' };
        return buildHomeDashboardModel();
      } finally {
        runtime.appState = previousState;
        runtime.configured = previousConfigured;
        runtime.user = previousUser;
      }
    },
    getHomeBalancesModelForState(state, preferences) {
      const previousState = runtime.appState;
      const previousConfigured = runtime.configured;
      const previousUser = runtime.user;
      const previousProfile = runtime.userProfile;
      try {
        runtime.appState = mergeState(state);
        runtime.configured = true;
        runtime.user = runtime.user || { uid: 'test-user' };
        runtime.userProfile = { preferences: preferences || {} };
        return getHomeBalancesModel();
      } finally {
        runtime.appState = previousState;
        runtime.configured = previousConfigured;
        runtime.user = previousUser;
        runtime.userProfile = previousProfile;
      }
    },
    getHomeBalanceAccountOptionsForState(state, preferences, query) {
      const previousState = runtime.appState;
      const previousConfigured = runtime.configured;
      const previousUser = runtime.user;
      const previousProfile = runtime.userProfile;
      try {
        runtime.appState = mergeState(state);
        runtime.configured = true;
        runtime.user = runtime.user || { uid: 'test-user' };
        runtime.userProfile = { preferences: preferences || {} };
        return getHomeBalanceAccountOptions(query);
      } finally {
        runtime.appState = previousState;
        runtime.configured = previousConfigured;
        runtime.user = previousUser;
        runtime.userProfile = previousProfile;
      }
    },
    aiAdvisorPreviousYearAvailable,
    deriveVat,
    formatVatDate,
    dashboardImportedOutstandingSplit,
    buildCompany,
    buildPayrollRunFromState,
    buildPayrollPostingRows,
    buildPayrollReadyTravelLines,
    payrollPayTypesForSettings,
    buildControlledBankLinePosting,
    normalizeBusinessLogoFile,
    businessLogoStoragePath,
    repairKnownDesignMockBusinessEmail,
    isLegacyDesignBusinessEmail,
    fixedAssetRows, fixedAssetBookCutoff,
    fixedAssetGroupRows,
    buildFixedAssetReclassifiedPurchase,
    withClientTimeout,
    listPartiesForDisplay,
    publishGlobals,
    clientStoreScopeMatches,
    isLegacyDesignQuoteLine,
    normalizeCustomerConversationThread,
    persistCleanedDesignFixtureLines,
    // Fakturautkastet fra timer, uten lagring. Lar testene verifisere at timer
    // blir til riktige fakturalinjer — mengde, pris, mva og salgskonto.
    buildTimeEntryInvoiceDraftForState(state, options) {
      const previousState = runtime.appState;
      try {
        runtime.appState = mergeState(state);
        return buildTimeEntryInvoiceDraft(options);
      } finally {
        runtime.appState = previousState;
      }
    },
  };
}

// Shared VAT due dates:
// MVA-modellen — maanedsknappene i AI Regnskapsforer trenger aa vite hvilken
// maaned en frist faller i. Én kilde: hadde de hatt sin egen tabell, ville
// sommerfristen 31. august for 3. termin divergert for eller siden.
window.plummeVatDueDate = vatDueDate;

window.PlummeBackend = {
  get configured() { return runtime.configured; },
  get user() { return runtime.user; },
  get userProfile() { return runtime.userProfile; },
  get memberships() { return runtime.memberships; },
  // Byraamedlemskap. Skallet leser dette for aa avgjore om byraaflaten skal vises.
  get firmMemberships() { return runtime.firmMemberships || []; },
  // Demokontoen kan opprette foretak og byraa uten registeroppslag. Flaten bruker
  // dette bare til aa vise navnefeltet — selve tillatelsen ligger i `demoAccount`-
  // claimet, som backend sjekker og en vanlig bruker ikke kan gi seg selv.
  get isDemoAccount() { return runtime.demoAccount === true; },
  get businessMembers() { return runtime.businessMembers; },
  get outgoingInvites() { return runtime.outgoingInvites; },
  get incomingInvites() { return runtime.incomingInvites; },
  get billing() { return runtime.billing; },
  get activeBusinessId() { return runtime.activeBusinessId; },
  get appState() { return runtime.appState; },
  get personnelStateReadable() { return runtime.personnelStateReadable; },
  publishGlobals,
  getCompany() {
    return buildCompany(mergeState(runtime.appState));
  },
  getHomeDashboardModel,
  getHomeBalancesModel,
  getHomeBalanceAccountOptions,
  setHomeBalanceAccounts,
  addHomeBalanceAccount,
  removeHomeBalanceAccount,
  getAiAdvisorModel,
  saveAiAdvisorLiquidityBuffer,
  getAiAdvisorSalesModel,
  getAiAdvisorPlanModel,
  getAdvisorGoals,
  saveAdvisorGoal,
  deleteAdvisorGoal,
  getPlannedCashEvents,
  savePlannedCashEvent,
  deletePlannedCashEvent,
  getAdvisorRecommendationStatuses, setAdvisorRecommendationStatus,
  getTaxDeductionHunter, saveTaxDeductionHunterItem, saveTaxDeductionHunterAnswers,
  getAiAdvisorOutcomesModel,
  getAiAdvisorReportTemplates,
  getAiAdvisorReportDoc,
  getSavedAiAdvisorReports, loadSavedAiAdvisorReports, getAiAdvisorReportLimit,
  saveAiAdvisorReport,
  deleteAiAdvisorReport,
  downloadAiAdvisorReportPdf, downloadAiAdvisorReportPptx,
  fetchAiAdvisorReportNarrative,
  createAiAdvisorReportTask,
  getAiAdvisorProfile,
  getAiAdvisorProfileOptions,
  saveAiAdvisorProfile,
  getAiAdvisorDismissLog,
  getAdvisorGreetingName,
  getAdvisorPayrollRates,
  getActiveBusinessProfile,
  fetchIndustryIndex,
  fetchAiAdvisorModel,
  fetchAiAdvisorInsights,
  askHomeAssistant,
  createAssistantInvoiceDraft,
  askAiAdvisorAssistant,
  askPersonalFinanceAssistant,
  getPersonalFinanceDailyAdvice,
  formatAssistantReplyForChat,
  getReportsModel,
  getTaxReturnModel, saveTaxReturnProgress, saveAssistantTaxAddition,
  mutateFixedAsset, setFixedAssetAutomation, getFixedAssetWorkModel, getYearClosingModel, saveYearClosingProgress, saveYearClosingCashCount, saveImportedFixedAssetReview, uploadYearClosingCountSheet,
  closeAccountingYear, currencyYearEnd,
  reopenAccountingYear,
  getAnnualAccountsModel,
  saveAnnualAccountsProgress,
  getCompanyRoles,
  saveCompanyRoles,
  fetchCompanyRolesFromRegistry,
  refreshCompanyRolesFromRegistry,
  getShareholders,
  saveShareholders,
  getAnnualMeetings,
  saveAnnualMeeting,
  downloadAnnualProtocolPdf,
  sendAnnualSignatureRequests,
  sendAnnualSignatureReminders,
  getAnnualSignatureStatus, downloadAnnualSignatureDocument,
  getBudgetModel,
  saveBudgetPlan,
  createBudgetFromPreviousYear,
  createStarterBudgetSuggestion,
  generateBudgetSuggestion,
  downloadReportPdf,
  getVatFilingModel,
  getFixedAssetsModel,
  getVouchersModel,
  getChartAccountsModel, saveCustomChartAccount, saveCustomChartAccountMappings,
  lookupBusinessRegistry,
  searchAddressLookup,
  searchMileageAddresses,
  calculateMileageDistance,
  reverseGeocode,
  getSalesVatOptions,
  getManualVatOptions,
  normalizeSalesVatOption,
  encodeSalesVatSelection,
  updateState, updateStateAndPersist,
  setLocalActiveYear,
  saveVehicleCostPolicy,
  mutateOpeningFigures, saveBusinessProfile, saveAccountingStartupProfile, saveOpeningBalance, completeOpeningBalance, analyzeOpeningBalanceImage, saveBankOpeningSetup,
  saveCustomsDeclaration,
  deleteCustomsDeclaration,
  annulCustomsDeclaration,
  markVatTermFiledOutside, getVatTermFiling, authorizedPost,
  saveVatPaymentRecord,
  deleteVatPaymentRecord,
  setEntity,
  getBankPageModel,
  getCurrentUserProfile,
  updateCurrentUserProfile,
  updateUserPreferencePatch,
  loadBillingSubscription,
  getInboundReceiptMailbox, getInboundInvestmentMailbox,
  rotateInboundReceiptMailbox,
  createCheckoutSession,
  createCustomerPortalSession,
  getStripeConnectIntegrationStatus,
  beginStripeConnectIntegration,
  disconnectStripeConnectIntegration,
  getZapierIntegrationStatus,
  generateZapierApiKey,
  disconnectZapierIntegration,
  getPartnerSettlementIntegrationStatus,
  generatePartnerSettlementApiKey,
  disconnectPartnerSettlementIntegration,
  getPartnerSettlementApiUrl,
  listPartnerSettlementBatches,
  approvePartnerSettlementBatch,
  resolveCommerceBatch,
  dismissCommerceBatch,
  approveCommerceBatch,
  listCommerceSettlements,
  getMcpAiIntegrationStatus,
  disconnectMcpAiIntegration,
  mcpActionApproval, getMcpAuthorizationRequest,
  approveMcpAuthorizationRequest,
  denyMcpAuthorizationRequest,
  getSlackIntegrationStatus,
  beginSlackIntegration,
  sendSlackTestMessage,
  saveSlackNotificationPreferences,
  disconnectSlackIntegration,
  getShopifyIntegrationStatus,
  beginShopifyIntegration,
  syncShopifyIntegration,
  listShopifyCommerceBatches,
  approveShopifyCommerceBatch,
  disconnectShopifyIntegration,
  getWooCommerceIntegrationStatus,
  connectWooCommerceIntegration,
  syncWooCommerceIntegration,
  listWooCommerceCommerceBatches,
  approveWooCommerceCommerceBatch,
  disconnectWooCommerceIntegration,
  getBusinessIntegrationsStatus,
  requestBusinessDeletion,
  uploadBusinessLogo,
  getBusinessLogoDownloadUrl,
  deleteBusinessLogo,
  sendPasswordResetEmail,
  createBusiness,
  checkBusinessAvailability,
  createPersonalFinanceClient,
  getPersonalFinanceHousehold,
  createPersonalFinanceHouseholdInvite,
  respondPersonalFinanceHouseholdInvite,
  cancelPersonalFinanceHouseholdInvite,
  hidePersonalFinanceHouseholdInvite,
  resendPersonalFinanceHouseholdInvite,
  leavePersonalFinanceHousehold,
  removeBusinessMember,
  leaveBusiness,
  getPersonalFinanceOverview,
  createCompleteAccountingExport,
  getLatestCompleteAccountingExport,
  uploadAccountingImportZip,
  createAccountingImportJob,
  getLatestAccountingImportJob,
  getAccountingImportPackages,
  deleteAccountingImportPackage,
  downloadAccountingImportPackage,
  removeAccountingImportYearData,
  getAccountingImportSalesHistory,
  settleAccountingImportOpenItem,
  settleAccountingImportSalesHistoryDocument,
  reanalyzeAccountingImportJob,
  commitAccountingImportJob,
  openAccountingImportDocument,
  uploadPersonalFinanceBankStatement,
  createPersonalFinanceImportJob,
  deletePersonalFinanceAccount,
  deletePersonalFinanceAsset,
  deletePersonalFinanceImportJob,
  deletePersonalFinanceLoan,
  linkPersonalFinanceImportJobAccount,
  recategorizePersonalFinanceTransactions,
  upsertPersonalFinanceAccount,
  upsertPersonalFinanceAsset,
  upsertPersonalFinanceLoan,
  updatePersonalFinancePlanning,
  updatePersonalFinanceTransactionCategory,
  createBusinessInvite,
  createBusinessOwnershipTransferInvite,
  cancelBusinessInvite,
  resendBusinessInvite,
  declineBusinessInvite,
  acceptBusinessInvite,
  getEmployeePortals,
  uploadEmployeePortalAttachment,
  getEmployeePortalAttachmentDownloadUrl,
  submitEmployeePortalExpense,
  mutateEmployeePortalTimeEntry,
  submitEmployeePortalTravel,
  mutateEmployeePortalTravel,
  manageEmployeePortalAccess,
  saveEmployee,
  saveEmployeeFixedPayrollLine,
  deleteEmployeeFixedPayrollLine,
  savePayrollSettings,
  savePayrollPayType,
  savePayrollRun,
  createPayrollRun, createPayrollRunCorrection,
  downloadPayrollPayslipPdf,
  sendPayrollRunPayslips,
  approvePayrollRun,
  markPayrollRunManualSubmission,
  submitPayrollRunAltinn,
  fetchPayrollNavRefunds,
  resetPayrollRunForEditing,
  annulPayrollRunAccounting,
  markPayrollRunPaid,
  savePayrollTaxPayment,
  annulPayrollTaxPayment,
  markPayrollRunPensionPaid, changePayrollPensionPayment, pensionInvoiceMutation,
  updatePayrollRunNote,
  addPayrollRunLine, deletePayrollRunLineItem,
  uploadPayrollRunDocument,
  attachPayrollRunInboxDocument,
  reversePayrollRun,
  deleteReversedPayrollRun,
  getPayrollRunPostings, getPayrollRunAccountingVouchers,
  updateEmployeePortalTravelStatus,
  saveEmployeePortalTravel, mutateMileage: (mileage) => mutateSettingsData({ action: 'mileage_workflow', resource: 'mileage', mileage }),
  deleteEmployeePortalTravel,
  listBusinessAuditLogs,
  createBankStatementFromFile, analyzeBankStatementImage, previewBankPeriodImport, importBankPeriodFile,
  archiveBankStatement, previewBankStatementArchive,
  reopenBankStatement,
  deleteBankStatement,
  assertCanBookBankStatement,
  approveBankMatchGroup,
  bookBankMatchGroup,
  cancelBankMatchGroup,
  reverseBankMatchGroup,
  getBankAllocationOpenItems,
  updateBankStatementLine,
  disableBankPattern,
  updateBankStatementLineImportCorrection,
  addBankStatementLine,
  updateBankStatementBalances,
  suggestBankStatementLines,
  refreshBankSuggestionRules,
  logBankSuggestionOutcome,
  logReceiptDraftCorrection,
  queueReceiptDraftCorrectionLearning,
  receiptLearningChangedFields,
  getAutopilotTaskQueue,
  approveAutopilotTaskQueue,
  approveAndBookAutopilotTasks,
  autoMatchBookedReceiptDuplicates,
  bookApprovedAutopilotTasks,
  updateAutopilotTaskProposal,
  getAutopilotSummary,
  getAutopilotPerformanceSnapshot,
  executeAutopilotTask,
  queueAutopilotTask,
  autopilotActionIsQueued,
  watchAutopilotJobs,
  getBookedReceiptDuplicateMatch,
  attachReceiptToBankLine,
  setBankLineReceiptRequirement,
  detachReceiptFromBankLine,
  reopenInvestmentBankMatch,
  resolveBankReconciliationPosting,
  correctBankLinkedPurchasePosting,
  attachReceiptToBankPosting,
  bookBankStatementLine,
  setOpenInvoice,
  consumeOpenInvoice,
  setOpenCustomerConversation,
  consumeOpenCustomerConversation,
  getSavedInvoiceComments,
  saveSavedInvoiceComments,
  buildInvoiceFromEditor,
  buildOtherSaleFromEditor,
  editorDraftKey,
  invoiceDraftExists,
  buildPurchaseFromEditor,
  upsertInvoice,
  upsertOtherSale,
  annulOtherSale,
  deleteOtherSaleDraft,
  upsertPurchase,
  getPurchaseDraftsModel, purchaseGrossAmount:dashboardGrossExpense, payForeignSupplierInvoice: (input) => mutateSupplierOverpayment('pay_foreign_supplier_invoice', input.expenseId, input),
  deletePurchaseDraft,
  annulPurchase,
  reclassifyFixedAssetPurchase,
  getReceiptInboxModel,
  patchReceiptInboxEntry,
  analyzeReceiptInboxEntry,
  bookReadyReceiptInboxEntries,
  getReceiptInboxBookingPlan,
  setReceiptInboxPaymentSource,
  applyBankPaymentSourceHints,
  receiptInboxBankPaymentSourceCandidates,
  findPossibleDuplicateExpenses,
  receiptSettlementOpenInvoiceMatches: receiptSettlementOpenInvoiceMatchSuggestions,
  findCollectionNoticeOriginalCandidates,
  findSupplierRecordByName,
  moveReceiptInboxEntriesToTrash,
  restoreReceiptInboxEntries,
  deleteReceiptInboxEntriesNow,
  moveReceiptInboxEntriesToDocumentation,
  downloadInboxAttachmentsArchive,
  getDocumentationInboxModel,
  uploadDocumentFileToInbox,
  moveDocumentationInboxEntriesToTrash,
  restoreDocumentationInboxEntries,
  deleteDocumentationInboxEntriesNow,
  uploadEmployeeDocument,
  openEmployeeDocument,
  downloadEmployeeDocument,
  deleteEmployeeDocument,
  getCompanyDocuments,
  uploadCompanyDocument,
  saveCompanyDocument,
  deleteCompanyDocument,
  openCompanyDocument,
  getDefaultSaleAttachments,
  saveFixedAssetRecord, getFixedAssetSalePreview,
  uploadEditorAttachments,
  uploadReceiptFileToInbox,
  // Bilag som kommer inn uten nedlastingslenke — e-postbilag lagres med
  // `storagePath` alene — trenger a kunne slaa den opp ved visning.
  resolveAttachmentUrl: getVoucherAttachmentUrl,
  mergeReceiptInboxEntries,
  splitReceiptInboxEntry,
  saveManualPostings, saveBankManualPosting, reimbursePrivatePurchase: (...args) => window.PlummeFinancialWorkflows.reimbursePrivatePurchase(...args), createSupplierCreditNote: (...args) => window.PlummeFinancialWorkflows.createSupplierCreditNote(...args), refundSupplierCreditNote: (...args) => window.PlummeFinancialWorkflows.refundSupplierCreditNote(...args),
  getManualPostingSettlementCandidates,
  getManualPostingCandidatesForOpenItem,
  settleManualPostingOpenItems,
  settleSupplierInvoiceWithoutPayment,
  refundSupplierOverpayment,
  reverseSupplierOverpaymentRefund,
  allocateSupplierOverpayment,
  reverseSupplierOverpaymentAllocation,
  annulManualPosting,
  correctManualPostingDate,
  correctManualPostingEntry,
  correctPostedManualPosting,
  getCustomerArchiveInfo,
  saveCustomerRecord,
  addCustomerContactPerson,
  archiveCustomerRecord,
  importCustomerRecords,
  saveProductRecord, updateInventoryCount,
  archiveProductRecord,
  importProductRecords,
  saveDocumentTextLineRecord,
  archiveDocumentTextLineRecord,
  getProjectTimeModel,
  saveProjectRecord,
  deleteProjectRecord,
  saveProjectActivity,
  deleteProjectActivity,
  saveTimeEntry,
  deleteTimeEntry,
  saveTimeTrackingSettings,
  createInvoiceDraftFromTimeEntries,
  releaseTimeEntriesFromInvoice,
  saveSupplierRecord,
  archiveSupplierRecord,
  importSupplierRecords,
  deleteReferenceDataRecords,
  resetAllBusinessData,
  importInvoiceRecords,
  importSaftMasterData,
  exportSaftFinancialXml,
  mergeCustomers,
  createCustomerConversationThread,
  saveCustomerConversationThread,
  loadCustomerConversationThreads,
  sendCustomerConversationEmail,
  createOfferFromCustomerConversation, ...globalThis.PlummeBackendSupport,
  mergeSuppliers,
  sendInvoiceDocument,
  sendInvoiceDocumentToCustomerList,
  sendOtherSaleDocument,
  downloadOtherSaleReceiptDocument,
  openInvoiceDocument,
  getInvoicePdfPreview,
  downloadInvoice,
  getPackingSlipPdfPreview,
  downloadPackingSlip,
  getInvoicePublicLink,
  getInvoicePublicView,
  openVoucherDocument,
  downloadVoucherDocument,
  uploadVoucherAttachment,
  markVoucherAttachmentLost,
  annulVoucher,
  getInvoiceListModel,
  getCustomerBillingModel,
  saveCustomerBillingGroup,
  saveRecurringInvoiceAgreement,
  setCustomerBillingGroupStatus,
  archiveCustomerBillingGroup,
  saveCustomerBillingGroupMembers,
  addCustomerBillingEmailRun,
  sendCustomerBillingGroupEmail,
  addInvoicePayment,
  replaceInvoicePayment,
  allocateCustomerCredit,
  reverseCustomerCreditAllocation,
  refundCustomerOverpayment,
  allocateCustomerOverpayment,
  reverseCustomerOverpaymentAllocation,
  registerAssistantPayment,
  reverseInvoicePayment,
  reverseInvoicePayments,
  copyInvoice,
  creditInvoice,
  issueInvoiceDraft,
  archiveInvoice,
  markInvoiceDeliveredExternally,
  markOfferAccepted,
  copyInvoiceForEditor,
  documentDraftFromOffer,
  deleteInvoice,
  markInvoicePaid,
  async signIn(email, password) {
    await initFirebase();
    await runtime.auth.signInWithEmailAndPassword(email, password);
  },
  async signUp({ email, password, displayName } = {}) {
    await initFirebase();
    const targetEmail = String(email || '').trim();
    const targetPassword = String(password || '');
    if (!targetEmail || !targetPassword) throw new Error(t('svc.emailAndPasswordRequired'));
    const credential = await runtime.auth.createUserWithEmailAndPassword(targetEmail, targetPassword);
    const nextName = String(displayName || '').trim();
    if (nextName && typeof credential.user?.updateProfile === 'function') {
      await credential.user.updateProfile({ displayName: nextName });
    }
    // Email-based access requires proof of ownership. A failed send does not undo
    // account creation; the verification screen exposes the failure and retry.
    if (typeof credential.user?.sendEmailVerification === 'function') {
      await sendEmailVerificationWithStatus(credential.user).catch((error) => {
        console.warn('[plumme-auth] Kunne ikke sende verifiseringsmail:', error);
      });
    }
    runtime.user = runtime.auth.currentUser || credential.user;
    emitRuntime();
    return runtime.user;
  },
  // Used when a flow is waiting for the signed-in address to be verified.
  async resendEmailVerification() {
    await initFirebase();
    const user = runtime.auth?.currentUser;
    if (!user) throw new Error(t('svc.needLoginProfile'));
    if (user.emailVerified) return false;
    await sendEmailVerificationWithStatus(user);
    return true;
  },
  async reloadEmailVerification() {
    await initFirebase();
    const user = runtime.auth?.currentUser;
    if (!user) return false;
    await user.reload();
    // Claims/token maa hentes pa nytt, ellers ser backend fortsatt email_verified=false.
    await user.getIdToken(true);
    runtime.user = runtime.auth.currentUser;
    emitRuntime();
    return runtime.auth.currentUser?.emailVerified === true;
  },
  async signOut() {
    // Rydd lytteren og den ventende lagringen FOR utlogging. Snapshot-guarden i
    // subscribeBusinessState hindrer at data fra forrige foretak vises, men en
    // abonnent mot et foretak brukeren ikke lenger er autentisert for har ingenting
    // a gjore der — og en debouncet lagring skal ikke fyre etter at okten er over.
    if (runtime.unsubscribeState) runtime.unsubscribeState();
    runtime.unsubscribeState = null;
    if (runtime.saveTimer) window.clearTimeout(runtime.saveTimer);
    runtime.saveTimer = null;
    // Samme grunn gjelder alle parkerte klienter: en lytter mot et foretak den
    // utloggede brukeren ikke lenger er autentisert for skal ikke leve videre, og
    // neste bruker i samme fane skal ikke kunne arve en parkert state.
    releaseAllClients();
    await runtime.auth?.signOut();
  },
  // ---------- Regnskapsbyra ----------
  // Byraflaten leser gjennom Functions, ikke direkte mot Firestore: reglene apner
  // bare klientstatus og medlemslista for byraaets medlemmer, og resten gaar via
  // backend nettopp for at en ansatt ikke skal kunne lese hele porteføljen ra.
  // Byraalista leses ved oppstart. Opprettes et byraa midt i okten, ma den hentes
  // pa nytt — ellers vet ikke skallet at brukeren na har et byraa, og «fant ikke
  // foretak» blir staaende som om ingenting hadde skjedd.
  async refreshFirmMemberships() {
    await loadFirmMemberships();
    emitRuntime();
    return runtime.firmMemberships;
  },
  async listAccountingFirmAccess() {
    if (!runtime.configured || !runtime.user) return { firms: [] };
    return authorizedPost('listAccountingFirmAccessHttp', {});
  },
  async listAccountingFirmClients(firmId) {
    if (!firmId) return { clients: [] };
    return authorizedPost('listAccountingFirmClientsHttp', { firmId });
  },
  // Overtakelsesdatoen avgjor hvor langt tilbake byraaets ansvar gaar. Den ligger
  // paa oppdraget, ikke paa klienten: to byraaer kan ha overtatt samme klient paa
  // hver sin dato, og klientens eget regnskap er uendret uansett.
  async setAccountingFirmEngagementTakeover({ firmId, businessId, overtakelsesdato } = {}) {
    return authorizedPost('setAccountingFirmEngagementTakeoverHttp', { firmId, businessId, overtakelsesdato: overtakelsesdato || '' });
  },
  // Klientoversiktens statusdokumenter. Leses direkte fordi flaten viser mange
  // klienter samtidig, og en Function per klient ville kostet en kaldstart hver.
  async listAccountingFirmClientStatus(firmId) {
    if (!runtime.db || !firmId) return [];
    const snapshot = await runtime.db.collection('accountingFirms').doc(firmId).collection('clientStatus').get();
    return snapshot.docs.map((entry) => ({ businessId: entry.id, ...(entry.data() || {}) }));
  },
  // Byraaets speil av klientdialogene. Leses direkte og som EN samling, av samme
  // grunn som klientstatusen: oversikten viser mange klienter samtidig, og et
  // oppslag per klient ville kostet en lesning per rad.
  async listAccountingFirmClientChatStatus(firmId) {
    if (!runtime.db || !firmId) return [];
    const snapshot = await runtime.db.collection('accountingFirms').doc(firmId).collection('clientChatStatus').get();
    return snapshot.docs.map((entry) => ({ businessId: entry.id, ...(entry.data() || {}) }));
  },
  async createAccountingFirm({ organizationNumber, country, firmName } = {}) {
    return authorizedPost('createAccountingFirmHttp', { organizationNumber, country, firmName });
  },
  // `title` MA sendes: backend utleder bade byraarolle og capabilities av stillingen,
  // og uten den falt en invitert kontrollor tilbake til vanlig `employee` uten
  // kontrolloerrettighet — stikk i strid med det byraaet valgte i skjemaet.
  async inviteAccountingFirmMember({ firmId, email, firmRole, capabilities, title } = {}) {
    return authorizedPost('inviteAccountingFirmMemberHttp', { firmId, email, firmRole, capabilities, title });
  },
  // Invitasjonen til selve byraaet. Aksepten ligger her og ikke i byraaflaten av en
  // enkel grunn: den inviterte har enna ingen byraaflate aa akseptere fra.
  async acceptAccountingFirmInvite(inviteId) {
    const result = await authorizedPost('acceptAccountingFirmInviteHttp', { inviteId });
    // Medlemskapet er det skallet leser for aa avgjore om byraaflaten skal vises.
    // Uten denne oppfriskningen matte den nyansatte laste appen pa nytt for aa se
    // byraaet sitt.
    await loadFirmMemberships();
    emitRuntime();
    return result;
  },
  async declineAccountingFirmInvite(inviteId) {
    return authorizedPost('declineAccountingFirmInviteHttp', { inviteId });
  },
  // Samme grunn som over: stillingen er det UI-et endrer, og den maa naa fram.
  async updateAccountingFirmMember({ firmId, memberUserId, firmRole, capabilities, title } = {}) {
    return authorizedPost('updateAccountingFirmMemberHttp', { firmId, memberUserId, firmRole, capabilities, title });
  },
  async removeAccountingFirmMember({ firmId, memberUserId, reason } = {}) {
    return authorizedPost('removeAccountingFirmMemberHttp', { firmId, memberUserId, reason });
  },
  async listAccountantMigrationCandidates() {
    return authorizedPost('listAccountantMigrationCandidatesHttp', {});
  },
  async migrateAccountantClientsToFirm({ firmId, businessIds } = {}) {
    return authorizedPost('migrateAccountantClientsToFirmHttp', { firmId, businessIds });
  },
  async createAccountingFirmEngagement({ firmId, businessId, organizationNumber, billedTo, gruppe } = {}) {
    return authorizedPost('createAccountingFirmEngagementHttp', { firmId, businessId, organizationNumber, billedTo, gruppe });
  },
  // Klientsiden av oppdraget. Aksepten er klientens, ikke byraaets — se
  // beslutning 9.2 i docs/features/regnskapsbyra-workspace-backlog.md.
  async listBusinessFirmEngagements(businessId = runtime.activeBusinessId) {
    if (!runtime.db || !businessId) return [];
    const snapshot = await runtime.db.collection('businesses').doc(businessId)
      .collection('accountingFirmEngagements').get();
    return snapshot.docs.map((entry) => ({ firmId: entry.id, ...(entry.data() || {}) }));
  },
  async acceptAccountingFirmEngagement({ firmId, businessId = runtime.activeBusinessId } = {}) {
    return authorizedPost('acceptAccountingFirmEngagementHttp', { firmId, businessId });
  },
  async declineAccountingFirmEngagement({ firmId, businessId = runtime.activeBusinessId, reason } = {}) {
    return authorizedPost('declineAccountingFirmEngagementHttp', { firmId, businessId, reason });
  },
  // ---------- Avstemming ----------
  async ensureReconciliationPeriod({ businessId = runtime.activeBusinessId, periodId } = {}) {
    return authorizedPost('ensureReconciliationPeriodHttp', buildReconciliationPeriodPayload({ businessId, periodId }));
  },
  async updateReconciliationAccount(payload = {}) {
    return authorizedPost('updateReconciliationAccountHttp', { businessId: runtime.activeBusinessId, ...payload });
  },
  async addReconciliationComment(payload = {}) {
    return authorizedPost('addReconciliationCommentHttp', { businessId: runtime.activeBusinessId, ...payload });
  },
  async setReconciliationLock(payload = {}) {
    return authorizedPost('setReconciliationLockHttp', { businessId: runtime.activeBusinessId, ...payload });
  },
  setReconciliationMateriality,
  setReconciliationAnalytics,
  confirmReconciliationCrossCheck,
  setReconciliationConclusion,
  uploadReconciliationDocument,
  removeReconciliationDocument,
  getReconciliationDocumentUrl,
  addReconciliationRequest,
  updateReconciliationRequest,
  listReconciliation,
  listFirmReconciliationStatus,
  // ---------- Kontroll ----------
  // Fire-oyne-regelen ligger i backend. Kallene her kan ikke omga den.
  async submitForControl(payload = {}) {
    return authorizedPost('submitForControlHttp', { businessId: runtime.activeBusinessId, ...payload });
  },
  async updateControlPoint(payload = {}) {
    return authorizedPost('updateControlPointHttp', payload);
  },
  async approveControl(payload = {}) {
    return authorizedPost('approveControlHttp', payload);
  },
  async returnControl(payload = {}) {
    return authorizedPost('returnControlHttp', payload);
  },
  async reopenControl(payload = {}) {
    return authorizedPost('reopenControlHttp', payload);
  },
  requestControlDocumentation, resolveControlDocumentation, assignControl, getCurrencyRate,
  // Innboksen leser byraaets tynne speil. Selve kontrollpunktene ligger under
  // regnskapet og hentes forst naar en kontroll aapnes — de skal bare leses av
  // den som har tilgang DIT.
  async listFirmControls(firmId) {
    if (!runtime.db || !firmId) return [];
    const snapshot = await runtime.db.collection('accountingFirms').doc(firmId).collection('controls').get();
    return snapshot.docs.map((entry) => ({ controlId: entry.id, ...(entry.data() || {}) }));
  },
  listBusinessControls,
  async getControl({ businessId, controlId } = {}) {
    if (!runtime.db || !businessId || !controlId) return null;
    const snapshot = await runtime.db.doc(`businesses/${businessId}/controls/${controlId}`).get();
    return snapshot.exists ? { controlId, ...(snapshot.data() || {}) } : null;
  },
  async listControlHistory({ businessId, controlId } = {}) {
    if (!runtime.db || !businessId || !controlId) return [];
    const snapshot = await runtime.db.collection(`businesses/${businessId}/controls/${controlId}/history`).get();
    return snapshot.docs
      .map((entry) => ({ id: entry.id, ...(entry.data() || {}) }))
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  },
  async endAccountingFirmEngagement({ firmId, businessId, reason } = {}) {
    return firmAssignmentMutation('endAccountingFirmEngagementHttp', { firmId, businessId, reason });
  },
  async assignAccountingFirmMember({ firmId, businessId, memberUserId, engagementRole } = {}) {
    return firmAssignmentMutation('assignAccountingFirmMemberHttp', { firmId, businessId, memberUserId, engagementRole });
  },
  // ---------- Dialog mellom byraaet og klienten ----------
  // Traaden ligger under FORETAKET (`businesses/{id}/firmChats/{firmId}`), ikke
  // under byraaet. Byraaets folk faar materialisert medlemskap i foretaket naar
  // oppdraget aksepteres, og mister det naar det avsluttes — saa tilgangen til
  // dialogen folger oppdraget helt av seg selv. Se firestore.rules.
  //
  // Meldinger skrives direkte fra klienten, som i teamchat: en Function per
  // tastetrykk ville lagt en mulig kaldstart paa hver melding.
  //
  // NAVNENE: `...FirmClientChat...`, ikke `...FirmChat...`. Teamchat lenger nede
  // eier allerede `subscribeFirmChatMessages` og `sendFirmChatMessage`. To like
  // noekler i samme objektliteral er ikke en feil i JavaScript — den siste vinner,
  // i stillhet — og byraadialogen kalte i praksis teamchatens funksjoner med helt
  // andre argumenter. `firmClientChatMethodNamesAreUnique` i
  // src/pages/firm-client-chat.test.js vokter dette naa.
  get firmEngagements() { return runtime.firmEngagements || []; },
  async refreshFirmEngagements() {
    await loadFirmEngagements();
    emitRuntime();
    return runtime.firmEngagements;
  },
  subscribeFirmClientChatThread({ businessId, firmId } = {}, onChange) {
    if (!runtime.db || !businessId || !firmId) return () => {};
    return runtime.db.collection('businesses').doc(businessId).collection('firmChats').doc(firmId)
      .onSnapshot(
        (snapshot) => onChange(snapshot.exists ? { firmId, ...(snapshot.data() || {}) } : null),
        (error) => {
          console.warn('[plumme-firm-chat] Kunne ikke lese traaden:', error);
          onChange(null);
        },
      );
  },
  // Support-uleste-telleren: tallet selv kommer fra en Cloud Functions-trigger
  // som speiler `supportTickets`-tellingen inn i et lite, per-foretak dokument
  // klienten faar lese (raa ticket-data er fortsatt lukket for klient-SDK-er).
  // Samme monster som `subscribeFirmClientChatThread` over.
  subscribeSupportUnreadCount({ businessId } = {}, onChange) {
    if (!runtime.db || !businessId) return () => {};
    return runtime.db.collection('businesses').doc(businessId).collection('supportTicketStatus').doc('summary')
      .onSnapshot(
        (snapshot) => onChange(snapshot.exists ? Number(snapshot.data()?.unreadCount) || 0 : 0),
        (error) => {
          console.warn('[plumme-support] Kunne ikke lese uleste-telleren:', error);
          onChange(0);
        },
      );
  },
  subscribeFirmClientChatMessages({ businessId, firmId } = {}, onChange) {
    if (!runtime.db || !businessId || !firmId) return () => {};
    return runtime.db.collection('businesses').doc(businessId).collection('firmChats').doc(firmId)
      .collection('messages')
      .orderBy('createdAt', 'asc')
      .limitToLast(200)
      .onSnapshot(
        (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }))),
        (error) => {
          console.warn('[plumme-firm-chat] Kunne ikke lese meldinger:', error);
          onChange([]);
        },
      );
  },
  // `side` avgjores av kalleren, men reglene validerer den mot `source`-feltet paa
  // medlemskapet. En klient kan altsaa ikke sende en melding merket 'firm', selv om
  // flaten skulle prove.
  async sendFirmClientChatMessage({ businessId, firmId, side, text, firmName } = {}) {
    const body = String(text || '').trim();
    if (!runtime.db || !runtime.user || !businessId || !firmId || !body) return null;
    if (side !== 'firm' && side !== 'client') return null;
    const threadRef = runtime.db.collection('businesses').doc(businessId).collection('firmChats').doc(firmId);
    const createdAt = nowIso();
    const message = {
      text: body.slice(0, 4000),
      authorUserId: runtime.user.uid,
      authorName: runtime.userProfile?.displayName || runtime.user.email || '',
      authorSide: side,
      createdAt,
    };
    await threadRef.collection('messages').add(message);
    // Traaddokumentet baerer siste melding, slik at klientoversikten og
    // hjemkortet kan vise «venter paa svar» uten aa abonnere paa hver melding.
    // Avsenderen markerer samtidig sin egen side som lest — hen har jo nettopp
    // sett den. Motpartens lesemerke er utilgjengelig for oss i reglene.
    await threadRef.set({
      firmId,
      ...(firmName ? { firmName: String(firmName).slice(0, 120) } : {}),
      lastMessageAt: createdAt,
      lastMessageText: message.text.slice(0, 140),
      lastMessageSide: side,
      ...(side === 'firm' ? { firmLastReadAt: createdAt } : { clientLastReadAt: createdAt }),
      updatedAt: createdAt,
    }, { merge: true });
    return message;
  },
  // Kalles naar panelet aapnes. Setter bare EGEN side sitt lesemerke — reglene
  // avviser et forsok paa aa markere motpartens.
  async markFirmClientChatRead({ businessId, firmId, side } = {}) {
    if (!runtime.db || !runtime.user || !businessId || !firmId) return false;
    if (side !== 'firm' && side !== 'client') return false;
    const readAt = nowIso();
    try {
      await runtime.db.collection('businesses').doc(businessId).collection('firmChats').doc(firmId).set({
        firmId,
        ...(side === 'firm' ? { firmLastReadAt: readAt } : { clientLastReadAt: readAt }),
        updatedAt: readAt,
      }, { merge: true });
      return true;
    } catch (error) {
      // Et lesemerke som ikke lot seg skrive skal ikke stenge samtalen.
      console.warn('[plumme-firm-chat] Kunne ikke sette lesemerke:', error);
      return false;
    }
  },

  // ---------- Teamchat i byraaet ----------
  // Meldinger skrives direkte fra klienten, med samme begrunnelse som kundedialogene
  // (se firestore.rules): en Function per tastetrykk ville lagt en mulig kaldstart
  // paa hver melding. Reglene validerer at avsenderen er seg selv, og en melding kan
  // ikke endres i ettertid.
  //
  // Direktemeldinger har rom-id `dm__<uid>__<uid>` med id-ene sortert. Da kan reglene
  // avgjore tilgang fra id-en alene, uten et oppslag mot romdokumentet for hver melding.
  firmDmRoomId(userIdA, userIdB) {
    const ids = [String(userIdA || ''), String(userIdB || '')].map((id) => id.trim()).filter(Boolean).sort();
    return ids.length === 2 ? `dm__${ids[0]}__${ids[1]}` : '';
  },
  subscribeFirmChatRooms(firmId, onChange) {
    if (!runtime.db || !firmId) return () => {};
    return runtime.db.collection('accountingFirms').doc(firmId).collection('chatRooms')
      .onSnapshot(
        (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }))),
        (error) => {
          console.warn('[plumme-firm-chat] Kunne ikke lese rom:', error);
          onChange([]);
        },
      );
  },
  subscribeFirmChatMessages(firmId, roomId, onChange) {
    if (!runtime.db || !firmId || !roomId) return () => {};
    return runtime.db.collection('accountingFirms').doc(firmId).collection('chatRooms').doc(roomId)
      .collection('messages')
      .orderBy('createdAt', 'asc')
      .limitToLast(200)
      .onSnapshot(
        (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }))),
        (error) => {
          console.warn('[plumme-firm-chat] Kunne ikke lese meldinger:', error);
          onChange([]);
        },
      );
  },
  async createFirmChatRoom({ firmId, name } = {}) {
    const roomName = String(name || '').trim();
    if (!runtime.db || !runtime.user || !firmId || !roomName) throw new Error(t('svc.mustBeSignedIn'));
    const ref = runtime.db.collection('accountingFirms').doc(firmId).collection('chatRooms').doc();
    await ref.set({
      name: roomName.slice(0, 80),
      kind: 'room',
      createdByUserId: runtime.user.uid,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    });
    return ref.id;
  },
  async sendFirmChatMessage({ firmId, roomId, text, roomName } = {}) {
    const body = String(text || '').trim();
    if (!runtime.db || !runtime.user || !firmId || !roomId || !body) return null;
    const roomRef = runtime.db.collection('accountingFirms').doc(firmId).collection('chatRooms').doc(roomId);
    const createdAt = nowIso();
    const message = {
      text: body.slice(0, 4000),
      authorUserId: runtime.user.uid,
      authorName: runtime.userProfile?.displayName || runtime.user.email || '',
      createdAt,
    };
    await roomRef.collection('messages').add(message);
    // Romdokumentet baerer siste melding, slik at romlista kan vise aktivitet uten
    // aa abonnere paa hver eneste traad.
    await roomRef.set({
      kind: roomId.startsWith('dm__') ? 'dm' : 'room',
      ...(roomName ? { name: String(roomName).slice(0, 80) } : {}),
      lastMessageAt: createdAt,
      lastMessageText: message.text.slice(0, 140),
      updatedAt: createdAt,
    }, { merge: true });
    return message;
  },
  async setActiveBusinessId(businessId) {
    if (!runtime.user || !runtime.memberships.some((membership) => membership.businessId === businessId)) return;
    if (businessId === runtime.activeBusinessId) return;
    // Den klienten vi forlater beholder state og lytter, sa veien tilbake er
    // umiddelbar. Er malet allerede apent, gjenbrukes det uten ny lasting.
    parkActiveClientState();
    runtime.activeBusinessId = businessId;
    localStorage.setItem(activeBusinessKey(runtime.user.uid), businessId);
    if (restoreClientState(businessId)) {
      publishGlobals(runtime.appState);
      emitRuntime();
      return;
    }
    subscribeBusinessState();
    emitRuntime();
  },
  // Apne klienter, for regnskapsforerflatens faner.
  listOpenClients() {
    return multiClientStore ? multiClientStore.listOpen() : [];
  },
  // Lukk en fane. Ogsa den aktive klienten kan lukkes — se `releaseClient` i
  // backend-client-tabs.jsx, som eier nedrivingen.
  closeClient(businessId) {
    return releaseClient(businessId);
  },
  subscribe(listener) {
    runtime.listeners.add(listener);
    listener({ ...runtime });
    return () => runtime.listeners.delete(listener);
  },
  useBackend() {
    const [snapshot, setSnapshot] = React.useState({ ...runtime });
    React.useEffect(() => window.PlummeBackend.subscribe(setSnapshot), []);
    React.useEffect(() => {
      if (runtime.bootStarted) return;
      runtime.bootStarted = true;
      const boot = async () => {
        try {
          const ok = await initFirebase();
          if (!ok) {
            runtime.loading = false;
            runtime.appState = mergeState(null);
            publishGlobals(runtime.appState);
            emitRuntime();
            return;
          }
          const authTimer = window.setTimeout(() => {
            if (!runtime.user && !runtime.activeBusinessId && runtime.loading) {
              runtime.loading = false;
              emitRuntime();
            }
          }, 6000);
          runtime.auth.onAuthStateChanged(async (user) => {
            window.clearTimeout(authTimer);
            window.PlummeStartupPerformance?.mark('auth-resolved');
            // Vaktbikkje for resten av oppstarten. Uten den blir skjermen staende pa
            // «laster» for alltid hvis et kall henger i stedet for a feile — for
            // eksempel nar Firebase Auth kjorer pa et domene som ikke staar i listen
            // over autoriserte domener. Den rydder seg selv: har oppstarten fullfort,
            // er `loading` allerede false og timeren gjor ingenting.
            // Hvilket steg vi star i. En oppstart som henger skal kunne si hvor den
            // stoppet — ellers har man bare en lasteskjerm og ingen trad a folge.
            // Fristen gjelder hvert steg, ikke hele oppstarten: en treg men fremadskridende oppstart skal ikke felles midt i et steg.
            const armBootWatchdog = (stage) => {
              runtime.bootStage = stage; window.clearTimeout(runtime.bootWatchdogTimer);
              runtime.bootWatchdogTimer = window.setTimeout(() => {
                if (!runtime.loading) return;
                runtime.loading = false; runtime.error = t('svc.bootTimeoutAt', { stage: runtime.bootStage || 'auth' });
                console.warn('[plumme-boot] Oppstart stoppet i steg:', runtime.bootStage); // for utvikleren, ikke brukeren
                emitRuntime();
              }, 20000);
            }; armBootWatchdog('auth');
            runtime.user = user;
            runtime.error = null;
            if (!user) {
              runtime.emailVerificationDelivery = null;
              runtime.userProfile = null;
              runtime.memberships = [];
              runtime.firmMemberships = [];
              runtime.demoAccount = false;
              runtime.businessMembers = [];
              runtime.outgoingInvites = [];
              runtime.incomingInvites = [];
              runtime.billing = null;
              runtime.activeBusinessId = null;
              runtime.appState = null;
              runtime.loading = false;
              emitRuntime();
              return;
            }
            runtime.loading = true;
            emitRuntime();
            // Callbacken er async, sa en feil her ville blitt en ubehandlet rejection
            // og latt `loading` sta pa true. Da ville brukeren fatt en evig
            // lasteskjerm uten et eneste spor i konsollen.
            try {
              armBootWatchdog('startupData'); await Promise.all([
                loadDemoAccountClaim().then(() => window.PlummeStartupPerformance?.mark('demo-claim-ready')),
                getCurrentUserProfile().catch(() => null).then(() => window.PlummeStartupPerformance?.mark('profile-ready')),
                loadBillingSubscription().then(() => window.PlummeStartupPerformance?.mark('billing-ready')),
                loadBusinessAccess().then(() => window.PlummeStartupPerformance?.mark('business-access-ready')),
              ]);
              armBootWatchdog('state');
              subscribeBusinessState();
            } catch (error) {
              runtime.error = error?.message || t('svc.bootFailed');
              runtime.loading = false;
              emitRuntime();
            }
          });
        } catch (error) {
          runtime.error = error.message || 'Could not initialize Firebase.';
          runtime.loading = false;
          emitRuntime();
        }
      }; void boot();
    }, []);
    return {
      ...snapshot,
      createPersonalFinanceImportJob,
      createCompleteAccountingExport,
      getLatestCompleteAccountingExport,
      createAccountingImportJob,
      getLatestAccountingImportJob,
      getAccountingImportPackages,
      deleteAccountingImportPackage,
      downloadAccountingImportPackage,
      removeAccountingImportYearData,
      getAccountingImportSalesHistory,
      settleAccountingImportOpenItem,
      reanalyzeAccountingImportJob,
      uploadAccountingImportZip,
      commitAccountingImportJob,
      openAccountingImportDocument,
      uploadVoucherAttachment,
      deletePersonalFinanceAccount,
      deletePersonalFinanceAsset,
      deletePersonalFinanceImportJob,
      deletePersonalFinanceLoan,
      getPersonalFinanceOverview,
      getPersonalFinanceDailyAdvice,
      askPersonalFinanceAssistant,
      getPersonalFinanceHousehold,
      createPersonalFinanceHouseholdInvite,
      respondPersonalFinanceHouseholdInvite,
      cancelPersonalFinanceHouseholdInvite,
      hidePersonalFinanceHouseholdInvite,
      resendPersonalFinanceHouseholdInvite,
      leavePersonalFinanceHousehold,
      linkPersonalFinanceImportJobAccount,
      recategorizePersonalFinanceTransactions,
      upsertPersonalFinanceAccount,
      upsertPersonalFinanceAsset,
      upsertPersonalFinanceLoan,
      updatePersonalFinancePlanning,
      updatePersonalFinanceTransactionCategory,
      uploadPersonalFinanceBankStatement,
    };
  },
};
